<i18n>
{
  "cl": {
    "feeDetail": "Detalle de tu cuota"
  },
  "pe": {
    "feeDetail": "Detalle de tu cuota"
  },
  "br": {
    "feeDetail": "Detalhe da parcela"
  }
}
</i18n>
<template>
  <div id="details" class="fee-item-details clearfix transition-slow" :class="{ show: isExtended }">
    <div
      v-if="$i18n.locale === 'cl' && payment.lateCharges > 0 && showRemoveLateCharges && noLateChargesRemainingDays <= 0"
      class="remove-late-charges details-footer">
      <h4 class="details-header big-size-text">Descontar gastos de cobranza hasta</h4>
      <b-field grouped group-multiline>
        <b-datepicker
          :id="`${payment.operationId}-${payment.paymentNumber}-no-late-charges-until-datepicker`"
          v-model="noLateChargesUntilAsDate"
          placeholder="hasta"
          class="is-primary is-two-thirds mobile-field"
          size="is-small"
          append-to-body
          rounded
          locale="es-ES" />
        <button
          class="button is-button-primary mobile-field is-rounded"
          @click="() => {
            $emit(
              'remove-late-charges',
              {
                operationId: payment.operationId,
                paymentNumber: payment.paymentNumber,
                noLateChargesUntil,
                companyOperationId: currentOperation.companyOperationId
              }
            )
          }">
          Descontar
        </button>
      </b-field>
    </div>
    <div
      v-if="payment.noLateChargesUntil && showRemoveLateCharges && noLateChargesRemainingDays >= 0">
      <button
        class="button is-button-primary mobile-field is-rounded"
        title="Reversar descuento de gasto de cobranza"
        @click="() => {
          $emit(
            'undo-remove-late-charges',
            {
              operationId: currentOperation._id,
              paymentNumber: payment.paymentNumber,
              companyOperationId: currentOperation.companyOperationId
            }
          )
        }">
        Reversar descuento
      </button>
    </div>
    <h4 class="details-header big-size-text">{{ $t('feeDetail') }}</h4>
    <div v-if="$i18n.locale === 'cl'" class="details-column1">
      <table class="table-data small-size-text">
        <tr>
          <td class="table-title">Amortización:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.amortization)}` }}</td>
        </tr>
        <tr>
          <td class="table-title">Interés:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.interest)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyDebtorLifeInsurance">
          <td class="table-title">{{ `${getlifeInsuranceLabel()}` }}</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.monthlyDebtorLifeInsurance)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyCodebtorLifeInsurance">
          <td class="table-title">Desgravamen codeudor:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.monthlyCodebtorLifeInsurance)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyUnemploymentInsurance">
          <td class="table-title">Seguro de cesantía:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.monthlyUnemploymentInsurance)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyFireInsurance">
          <td class="table-title">Seguro incendio y sismo:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.monthlyFireInsurance)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyCreditInsurance">
          <td class="table-title">Seguro de Crédito:</td>
          <td id="monthly-credit-insurance">{{ `${monetarySign} ${formatNumber(payment.monthlyCreditInsurance)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyCreditInsurancePrize">
          <td class="table-title">Seguro de Crédito (Premio):</td>
          <td id="monthly-credit-insurance">{{ `${monetarySign} ${formatNumber(payment.monthlyCreditInsurancePrize)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyCreditInsuranceExpenses">
          <td class="table-title">Seguro de Crédito (Costo):</td>
          <td id="monthly-credit-insurance">{{ `${monetarySign} ${formatNumber(payment.monthlyCreditInsuranceExpenses)}` }}</td>
        </tr>
        <tr v-if="!!payment.lateInterest">
          <td class="table-title">Interés por mora:</td>
          <td>
            {{ `${monetarySign}
            ${formatNumber(payment[`${type === 'MORTGAGE' || (operation.considerLate || operation.considerLateInterest) ? 'lateInterest' : 'secondaryCurrencyLateInterest'}`])}` }}
          </td>
        </tr>
        <tr v-if="!!payment.lateCharges || (noLateChargesRemainingDays > 0)">
          <td class="table-title">Gastos de Cobranza:</td>
          <td id="late-charges" class="has-text-right">
            {{ `${monetarySign}
            ${formatNumber(payment[`${type === 'MORTGAGE' || (operation.considerLate || operation.considerLateCharges) ? 'lateCharges' : 'secondaryCurrencyLateCharges'}`])}` }} <br>
            {{
              noLateChargesRemainingDays > 0 ? `(liberado hasta ${dateToFormatedString(payment.noLateChargesUntil)})` : ''
            }}
          </td>
        </tr>
      </table>
    </div>
    <div v-if="$i18n.locale === 'br'" class="details-column1">
      <table class="table-data small-size-text">
        <tr>
          <td class="table-title">Amortização:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.amortizationIUB)}` }}</td>
        </tr>
        <tr>
          <td class="table-title">Juros:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.interestIUB)}` }}</td>
        </tr>
        <tr v-if="!!payment.debtorLifeInsuranceFee">
          <td class="table-title">Seguro MIP:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.debtorLifeInsuranceFee)}` }}</td>
        </tr>
        <tr v-if="!!payment.codebtorLifeInsuranceFee">
          <td class="table-title">Seguro MIP Codeudor:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.codebtorLifeInsuranceFee)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyFireInsurance">
          <td class="table-title">Seguro DFI:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.monthlyFireInsurance)}` }}</td>
        </tr>
        <tr v-if="!!payment.monthlyCreditInsurance">
          <td class="table-title">Seguro de Credito:</td>
          <td id="monthly-credit-insurance">{{ `${monetarySign} ${formatNumber(payment.monthlyCreditInsurance)}` }}</td>
        </tr>
        <tr v-if="!!payment.lateChargesFine">
          <td class="table-title">Multa por atraso:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.lateChargesFine)}` }}</td>
        </tr>
        <tr v-if="!!payment.totalLateInterest || (noLateChargesRemainingDays > 0)">
          <td class="table-title">Juros por atraso:</td>
          <td id="late-charges" class="has-text-right">
            {{ `${monetarySign} ${formatNumber(payment.totalLateInterest)}` }} <br>
            {{
              noLateChargesRemainingDays > 0 ? `(liberado hasta ${dateToFormatedString(payment.noLateChargesUntil)})` : ''
            }}
          </td>
        </tr>
      </table>
    </div>
    <div v-if="$i18n.locale === 'pe'" class="details-column1">
      <table class="table-data small-size-text">
        <tr>
          <td class="table-title">Amortización:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.amortization)}` }}</td>
        </tr>
        <tr>
          <td class="table-title">Fecha de Vencimento:</td>
          <td>{{ payment.dueDate }}</td>
        </tr>
        <tr>
          <td class="table-title">Cuota Mensual:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.monthlyPayment)}` }}</td>
        </tr>
      </table>
    </div>
    <div v-if="$i18n.locale === 'cl'" class="details-column2">
      <table class="table-data small-size-text">
        <tr class="med-size-text">
          <td class="table-title">Cuota:</td>
          <td>{{ payment.paymentNumber + '/' + currentOperation?.termAsMonths }}</td>
        </tr>
        <tr v-if="type !== 'COMPLEMENTARY'" class="med-size-text">
          <td class="table-title">Total en UF:</td>
          <td class="significant-data">{{ `${monetarySign} ${formatNumber(payment.totalMonthlyPayment)}` }}</td>
        </tr>
        <tr v-if="type !== 'COMPLEMENTARY'">
          <td class="table-title">Valor UF del día:</td>
          <td>$ {{ formatNumber(payment.secondaryCurrencyFactor) }}</td>
        </tr>
        <tr class="med-size-text">
          <td class="table-title">Total{{ type === 'MORTGAGE' || type === 'PARKING' ? ' en pesos:' : ':' }}</td>
          <td>{{ `$ ${formatNumber(payment.secondaryCurrencyTotalMonthlyPayment)}` }}</td>
        </tr>
      </table>
    </div>
    <div v-if="$i18n.locale === 'br'" class="details-column2">
      <table class="table-data small-size-text">
        <tr class="med-size-text">
          <!-- <td class="table-title">Total{{ type === 'MORTGAGE' ? ' en pesos:' : ':' }}</td> -->
          <td class="table-title">Valor total da parcela:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.totalMonthlyPayment)}` }}</td>
        </tr>
      </table>
    </div>
    <div v-if="$i18n.locale === 'pe'" class="details-column2">
      <table class="table-data small-size-text">
        <tr>
          <td class="table-title">Cargos por Mora:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.lateCharges)}` }}</td>
        </tr>
        <tr>
          <td class="table-title">Días de Mora:</td>
          <td>{{ formatNumber(payment.lateDays) }}</td>
        </tr>
        <tr class="med-size-text">
          <td class="table-title">Valor total de la cuota:</td>
          <td>{{ `${monetarySign} ${formatNumber(payment.totalMonthlyPayment)}` }}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import { dateToFormatedString, getDaysDifferenceBetweenDays } from '@/infrastructure/date';
import { formatNumber } from '@/infrastructure/format';

export default {
  props: {
    isExtended: { type: Boolean, default: false },
    showRemoveLateCharges: { type: Boolean, default: false },
    operation: { type: Object, default: () => ({}) },
    payment: { type: Object, default: () => ({}) },
    type: { type: String, default: '' },
    monetarySign: { type: String, default: '' }
  },
  data() {
    const today = new Date();
    return {
      noLateChargesUntilAsDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3),
      today
    };
  },
  computed: {
    noLateChargesRemainingDays() {
      if (!this.payment.noLateChargesUntil) {
        return 0;
      }
      return getDaysDifferenceBetweenDays(this.today, this.payment.noLateChargesUntil);
    },
    noLateChargesUntil() {
      return this.noLateChargesUntilAsDate.toISOString().substring(0, 10);
    },
    currentOperation() {
      return this.operation._id === this.payment.operationId ? this.operation : this.operation.complementaryOperation;
    }
  },
  methods: {
    // TODO: Remover este hack cuando se tenga llave debtorDisabilityInsuranceType
    getlifeInsuranceLabel() {
      if (this.currentOperation.debtorLifeInsuranceType.includes('disability')) {
        return 'Seguro de invalidez:';
      }
      return 'Seguro de desgravamen:';
    },
    formatNumber: number => formatNumber({ number }),
    dateToFormatedString: (date, format = 'DD/MM/YYYY') => dateToFormatedString(date, format)
  }
};
</script>

<style scoped>
.big-size-text {
  font-size: 1.2rem;
}
.med-size-text {
  font-size: 0.9rem;
}
.small-size-text {
  font-size: 0.75rem;
}

/* item cuota - detalle */
.fee-item-details {
  display: grid;
  grid:
    "header header" auto
    "col1 col2" auto
    "footer footer" auto / minmax(auto, 1fr) minmax(auto, 1fr);
  gap: 0.5rem 10%;
  place-content: stretch center;
  place-items: center start;

  background-color: #F9F9F9;
  border-radius: 1.5rem;
  margin-top: 0rem;
  padding: 0rem 1.5rem;
  width: 100%;
  min-width: 10rem;
  max-height: 0rem;
  overflow: hidden;
}

.fee-item-details.show {
  max-height: 100rem;
  margin-top: 3%;
  padding: 3% 5%;
  overflow-y: auto;
  overflow: visible;
}

.fee-item-details h4 {
  overflow: hidden;
  margin: 0rem;
}

.fee-item-details .details-header {
  grid-area: header;
  place-self: start stretch;
}

.fee-item-details .details-footer {
  grid-area: footer;
  place-self: end stretch;
  margin-top: 2em;
}

.fee-item-details .details-column1 {
  width: 100%;
  grid-area: col1;
  place-self: start center;
}

.fee-item-details .details-column2 {
  width: 100%;
  grid-area: col2;
  place-self: start center;
}

.fee-item-details table {
  display: flex;
  flex-flow: column nowrap;
}

.fee-item-details table tr {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  gap: 0.3rem;
}

table .table-title {
  color: var(--primary);
}

.mobile-field {
  height: 2.6em;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
}

@media (max-width: 1000px) {
  .big-size-text {
    font-size: 1rem;
  }

  .med-size-text {
    font-size: 0.75rem;
  }

  .small-size-text {
    font-size: 0.65rem;
  }
}

@media (max-width: 900px) {
  .big-size-text {
    font-size: 1.2rem;
  }

  .med-size-text {
    font-size: 0.9rem;
  }

  .small-size-text {
    font-size: 0.75rem;
  }

  .fee-item-details .details-column1,
  .fee-item-details .details-column1 table,
  .fee-item-details .details-column2,
  .fee-item-details .details-column2 table {
    width: 100%;
  }
}

@media (max-width: 782px) {
  .mobile-field {
    width: 100%;
    flex-grow: 1;
    margin-right: 0 !important;
  }
  .card-item .fee-item-details {
  grid:
    "header header" auto
    "col1 col2" auto
    "footer footer" auto
    / 1.3fr 1fr;
  }
}
@media (max-width: 660px) {
  .card-item .fee-item-details {
    grid:
      "header" auto
      "col1" auto
      "col2" auto
      "footer" auto
      / 1fr;
    gap: 0.5rem 0rem;
  }
}

@media (max-width: 380px) {
  .big-size-text {
    font-size: 1rem;
  }

  .med-size-text {
    font-size: 0.75rem;
  }

  .small-size-text {
    font-size: 0.65rem;
  }
}
</style>
